/**
 * Geo-Currency by visitor location.
 *
 * Catalog / checkout = USD (Final_USD from sheet).
 * India display = Final_INR from sheet only (SKU map published by pricing sync).
 *   No USD→INR fallback conversion.
 *
 *   India  → sheet Final_INR by SKU
 *   Canada → USD → CAD at live rate
 *   else   → catalog USD as-is
 */
(function () {
  'use strict';

  const CACHE_KEY = 'geo_currency_rates_usd_v3';
  const CACHE_HOURS = 12;
  const RATES_URL = 'https://open.er-api.com/v6/latest/INR';
  const FALLBACK_COUNTRY_URL = 'https://ipapi.co/json/';

  const COUNTRY_TO_CURRENCY = {
    IN: 'INR',
    CA: 'CAD'
  };

  const SYMBOLS = {
    USD: '$',
    CAD: 'CA$',
    INR: '₹'
  };

  const LOCALES = {
    INR: 'en-IN',
    CAD: 'en-CA',
    USD: 'en-US'
  };

  var state = {
    country: null,
    currency: 'USD',
    rate: 1,
    usdPerInr: null,
    rates: null,
    /** SKU → Final_INR from sheet */
    inrBySku: {},
    mapReady: false
  };

  function getCachedRates() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts > CACHE_HOURS * 60 * 60 * 1000) return null;
      return parsed.data;
    } catch (_) {
      return null;
    }
  }

  function setCachedRates(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
    } catch (_) {}
  }

  function detectCountry(cb) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.country) {
      var c = window.Shopify.country;
      cb(typeof c === 'string' ? c : (c && c.iso_code ? c.iso_code : null));
      return;
    }
    var xhr = new XMLHttpRequest();
    xhr.open('GET', FALLBACK_COUNTRY_URL, true);
    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4) return;
      try {
        var j = JSON.parse(xhr.responseText);
        cb(j && j.country_code ? j.country_code : null);
      } catch (_) {
        cb(null);
      }
    };
    xhr.onerror = function () { cb(null); };
    xhr.send();
  }

  function fetchRates(cb) {
    var cached = getCachedRates();
    if (cached && cached.rates && cached.base === 'INR') {
      cb(cached);
      return;
    }
    var xhr = new XMLHttpRequest();
    xhr.open('GET', RATES_URL, true);
    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4) return;
      try {
        var j = JSON.parse(xhr.responseText);
        var data = j && j.rates ? { base: 'INR', rates: j.rates } : null;
        if (data) setCachedRates(data);
        cb(data || cached);
      } catch (_) {
        cb(cached);
      }
    };
    xhr.onerror = function () { cb(getCachedRates()); };
    xhr.send();
  }

  function mergeInrMap(obj) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;
    // Handle accidental double-encoded JSON string
    if (typeof obj === 'string') {
      try {
        obj = JSON.parse(obj);
      } catch (_) {
        return;
      }
    }
    for (var sku in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, sku)) continue;
      var n = Number(obj[sku]);
      if (!isFinite(n)) continue;
      var key = String(sku).trim();
      state.inrBySku[key] = Math.round(n);
      state.inrBySku[key.toUpperCase()] = Math.round(n);
    }
  }

  function parseJsonFlexible(text) {
    var raw = String(text || '').trim();
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (_) {}
      }
      return parsed;
    } catch (_) {
      return null;
    }
  }

  /** Load Final_INR map: inline shop metafield + theme asset (always try both). */
  function loadInrMap(done) {
    var nodes = document.querySelectorAll('[data-silvify-inr-by-sku]');
    for (var i = 0; i < nodes.length; i++) {
      mergeInrMap(parseJsonFlexible(nodes[i].textContent));
    }

    var assetUrl = window.__SILVIFY_INR_ASSET__;
    if (!assetUrl) {
      state.mapReady = true;
      if (window.__SILVIFY_GEO_DEBUG__) {
        console.info('[silvify-geo] INR map size', Object.keys(state.inrBySku).length / 2);
      }
      done();
      return;
    }

    var xhr = new XMLHttpRequest();
    // cache-bust so preview picks up sync uploads
    var url = assetUrl + (assetUrl.indexOf('?') >= 0 ? '&' : '?') + 'v=' + Date.now();
    xhr.open('GET', url, true);
    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4) return;
      try {
        if (xhr.status >= 200 && xhr.status < 300) {
          mergeInrMap(parseJsonFlexible(xhr.responseText));
        }
      } catch (_) {}
      state.mapReady = true;
      var size = Object.keys(state.inrBySku).length;
      if (window.__SILVIFY_GEO_DEBUG__ || size === 0) {
        console.info('[silvify-geo] INR map entries', size, 'currency', state.currency);
      }
      done();
    };
    xhr.onerror = function () {
      state.mapReady = true;
      done();
    };
    xhr.send();
  }

  function resolveSku(el) {
    var sku = el.getAttribute('data-sku');
    if (sku) return String(sku).trim();
    var form = el.closest('form') || document;
    var input = form.querySelector('[data-sku]');
    if (input && input.getAttribute('data-sku')) return String(input.getAttribute('data-sku')).trim();
    return null;
  }

  function lookupInr(sku) {
    if (!sku) return null;
    var key = String(sku).trim();
    if (state.inrBySku[key] != null) return state.inrBySku[key];
    if (state.inrBySku[key.toUpperCase()] != null) return state.inrBySku[key.toUpperCase()];
    return null;
  }

  /** Sheet Final_INR for this element; null if SKU not in sheet map. */
  function sheetInrForEl(el) {
    var sku = resolveSku(el);
    var unit = lookupInr(sku);
    if (unit == null) return null;
    var qty = parseFloat(el.getAttribute('data-qty'), 10);
    if (isFinite(qty) && qty > 0) return unit * qty;
    return unit;
  }

  function sheetInrRange(el) {
    var raw = el.getAttribute('data-skus');
    if (!raw) return null;
    var skus = raw.split(',');
    var min = null;
    var max = null;
    for (var i = 0; i < skus.length; i++) {
      var n = lookupInr(skus[i]);
      if (n == null) continue;
      if (min == null || n < min) min = n;
      if (max == null || n > max) max = n;
    }
    if (min == null) return null;
    return { min: min, max: max };
  }

  function luxuryRoundCad(amount) {
    if (!isFinite(amount) || amount <= 0) return amount;
    if (amount % 10 === 9) return amount;
    var roundedUp = Math.floor(amount / 10) * 10 + 9;
    if (roundedUp < amount) roundedUp += 10;
    return roundedUp;
  }

  function formatPrice(value, currency) {
    var s = SYMBOLS[currency] || currency + ' ';
    var locale = LOCALES[currency] || undefined;
    var amount = Number(value);
    var isWhole = Math.abs(amount - Math.round(amount)) < 0.001;
    var digits = currency === 'INR' || isWhole ? 0 : 2;
    return s + amount.toLocaleString(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }

  function formatRateLine() {
    if (state.currency === 'CAD' && state.rate > 0) {
      return 'Exchange rate: 1 USD = CA$' + Number(state.rate).toFixed(2) + '. Your bank may apply its own rate.';
    }
    if (state.currency === 'INR' && state.usdPerInr > 0) {
      var inrPerUsd = Math.round(1 / state.usdPerInr);
      return (
        'Exchange rate: 1 USD ≈ ₹' +
        inrPerUsd.toLocaleString('en-IN') +
        '. India prices are Final_INR from our price sheet.'
      );
    }
    return '';
  }

  var CUSTOMS_NOTE =
    'International orders may be subject to customs duties or local taxes depending on destination.';

  function formatExchangeNotice() {
    if (state.currency === 'CAD' || state.currency === 'INR') {
      return 'Prices shown in ' + state.currency + ' for convenience. You will be charged in USD at checkout. ' + CUSTOMS_NOTE;
    }
    return 'Checkout is charged in USD. ' + CUSTOMS_NOTE;
  }

  function findProductUsdAmount(panel) {
    var scope =
      (panel && panel.closest('product-form-component, .product-information, .product-details, main')) ||
      document;
    var priceEl =
      scope.querySelector('.price[data-base-price]:not([data-base-price-min])') ||
      scope.querySelector('[data-base-price]:not([data-base-price-min])');
    if (!priceEl) return null;
    var n = parseFloat(priceEl.getAttribute('data-base-price'), 10);
    return isNaN(n) ? null : n;
  }

  function updateFxPanels() {
    var showFx = state.currency === 'CAD' || state.currency === 'INR';
    var rateLine = formatRateLine();
    var panels = document.querySelectorAll('[data-geo-fx]');

    for (var i = 0; i < panels.length; i++) {
      var panel = panels[i];
      panel.hidden = !showFx;
      if (!showFx) continue;

      var rateEl = panel.querySelector('[data-geo-fx-rate]');
      if (rateEl) rateEl.textContent = rateLine;

      var customsEl = panel.querySelector('[data-geo-fx-customs]');
      if (customsEl) customsEl.textContent = CUSTOMS_NOTE;

      var usdTotalEl = panel.querySelector('[data-geo-usd-total]');
      if (usdTotalEl) {
        var amt = parseFloat(usdTotalEl.getAttribute('data-usd-amount'), 10);
        if (!isNaN(amt)) usdTotalEl.textContent = formatPrice(amt, 'USD');
      }

      var usdProductEl = panel.querySelector('[data-geo-usd-from-product]');
      if (usdProductEl) {
        var productUsd = findProductUsdAmount(panel);
        usdProductEl.textContent = productUsd != null ? formatPrice(productUsd, 'USD') : '—';
      }
    }

    var taxNotes = document.querySelectorAll('.cart__summary-item.tax-note');
    for (var t = 0; t < taxNotes.length; t++) {
      taxNotes[t].hidden = showFx;
    }
  }

  function updateExchangeNotices() {
    var text = formatExchangeNotice();
    var notices = document.querySelectorAll('.geo-currency-notice');
    for (var i = 0; i < notices.length; i++) {
      var host = notices[i].closest('.buy-buttons-block, product-form-component');
      if (host && host.querySelector('[data-geo-fx]') && (state.currency === 'CAD' || state.currency === 'INR')) {
        notices[i].hidden = true;
        continue;
      }
      notices[i].hidden = false;
      notices[i].textContent = text;
    }
    updateFxPanels();
  }

  function convertAndDisplay() {
    var root = document.body;
    if (!root) return;

    var withBase = root.querySelectorAll('[data-base-price]');
    for (var i = 0; i < withBase.length; i++) {
      var el = withBase[i];
      if (el.getAttribute('data-base-price-min') || el.getAttribute('data-base-price-max')) continue;
      if (el.hasAttribute('data-silvify-cart-inr-total')) continue;

      var base = parseFloat(el.getAttribute('data-base-price'), 10);
      if (isNaN(base)) continue;

      if (state.currency === 'INR') {
        var inr = sheetInrForEl(el);
        if (inr != null) {
          el.textContent = formatPrice(inr, 'INR');
        } else {
          // No sheet row for this SKU — show USD, never invent INR
          el.textContent = formatPrice(base, 'USD');
        }
        continue;
      }

      if (state.currency === 'CAD') {
        el.textContent = formatPrice(luxuryRoundCad(base * state.rate), 'CAD');
        continue;
      }

      el.textContent = formatPrice(base, 'USD');
    }

    var minMax = root.querySelectorAll('[data-base-price-min][data-base-price-max]');
    for (var j = 0; j < minMax.length; j++) {
      var rangeEl = minMax[j];
      var min = parseFloat(rangeEl.getAttribute('data-base-price-min'), 10);
      var max = parseFloat(rangeEl.getAttribute('data-base-price-max'), 10);
      if (isNaN(min) || isNaN(max)) continue;

      if (state.currency === 'INR') {
        var range = sheetInrRange(rangeEl);
        if (range) {
          rangeEl.textContent =
            formatPrice(range.min, 'INR') + ' - ' + formatPrice(range.max, 'INR');
        } else {
          rangeEl.textContent = formatPrice(min, 'USD') + ' - ' + formatPrice(max, 'USD');
        }
        continue;
      }

      if (state.currency === 'CAD') {
        rangeEl.textContent =
          formatPrice(luxuryRoundCad(min * state.rate), 'CAD') +
          ' - ' +
          formatPrice(luxuryRoundCad(max * state.rate), 'CAD');
        continue;
      }

      rangeEl.textContent = formatPrice(min, 'USD') + ' - ' + formatPrice(max, 'USD');
    }

    var compare = root.querySelectorAll('[data-base-compare-price]');
    for (var k = 0; k < compare.length; k++) {
      var cmpEl = compare[k];
      var cmpBase = parseFloat(cmpEl.getAttribute('data-base-compare-price'), 10);
      if (isNaN(cmpBase)) continue;
      if (state.currency === 'INR') {
        var cmpInr = sheetInrForEl(cmpEl);
        cmpEl.textContent = formatPrice(cmpInr != null ? cmpInr : cmpBase, cmpInr != null ? 'INR' : 'USD');
      } else if (state.currency === 'CAD') {
        cmpEl.textContent = formatPrice(luxuryRoundCad(cmpBase * state.rate), 'CAD');
      } else {
        cmpEl.textContent = formatPrice(cmpBase, 'USD');
      }
    }

    // Cart estimated total = sum of sheet INR line totals
    var cartTotal = root.querySelector('[data-silvify-cart-inr-total]');
    if (cartTotal && state.currency === 'INR') {
      var lines = root.querySelectorAll('[data-silvify-line-total][data-sku]');
      var sum = 0;
      var ok = lines.length > 0;
      for (var L = 0; L < lines.length; L++) {
        var lineInr = sheetInrForEl(lines[L]);
        if (lineInr == null) {
          ok = false;
          break;
        }
        sum += lineInr;
      }
      if (ok) {
        cartTotal.textContent = formatPrice(sum, 'INR');
      } else {
        var usdTotal = parseFloat(cartTotal.getAttribute('data-base-price'), 10);
        if (!isNaN(usdTotal)) cartTotal.textContent = formatPrice(usdTotal, 'USD');
      }
    } else if (cartTotal && state.currency === 'CAD') {
      var usdCart = parseFloat(cartTotal.getAttribute('data-base-price'), 10);
      if (!isNaN(usdCart)) {
        cartTotal.textContent = formatPrice(luxuryRoundCad(usdCart * state.rate), 'CAD');
      }
    } else if (cartTotal) {
      var usdOnly = parseFloat(cartTotal.getAttribute('data-base-price'), 10);
      if (!isNaN(usdOnly)) cartTotal.textContent = formatPrice(usdOnly, 'USD');
    }

    updateExchangeNotices();
    updateUsdAlongside();
  }

  function updateUsdAlongside() {
    var show = state.currency === 'CAD' || state.currency === 'INR';
    var els = document.querySelectorAll('[data-usd-alongside]');
    for (var i = 0; i < els.length; i++) {
      els[i].hidden = !show;
    }
  }

  function applyCurrencyAndRates(data) {
    state.rates = data && data.rates ? data.rates : null;
    state.usdPerInr = state.rates && isFinite(state.rates.USD) ? state.rates.USD : null;

    if (state.currency === 'CAD' && state.rates && state.usdPerInr > 0 && isFinite(state.rates.CAD)) {
      state.rate = state.rates.CAD / state.usdPerInr;
    } else {
      state.rate = 1;
    }

    convertAndDisplay();
  }

  function currencyForCountry(countryCode) {
    if (!countryCode) return 'USD';
    return COUNTRY_TO_CURRENCY[String(countryCode).toUpperCase()] || 'USD';
  }

  function run() {
    detectCountry(function (countryCode) {
      state.country = countryCode;
      state.currency = currencyForCountry(countryCode);

      loadInrMap(function () {
        if (state.currency === 'USD') {
          state.rate = 1;
          convertAndDisplay();
          return;
        }
        // CAD needs FX; INR only needs rates for the informational exchange-rate line
        fetchRates(function (data) {
          applyCurrencyAndRates(data);
        });
      });
    });
  }

  function onReady() {
    run();

    var observer = new MutationObserver(function (mutations) {
      var hasNew = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          hasNew = true;
          break;
        }
      }
      if (hasNew) setTimeout(convertAndDisplay, 100);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('cart:update', function () {
      setTimeout(convertAndDisplay, 50);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
